/**
 * StructureSetDisplayObject2D — Renders a set of structures on 2D slices.
 *
 * Combines two rendering techniques in one display object:
 * 1. Label map overlay (vtkImageSlice on renderer layer 1) — colored regions
 *    with per-label opacity driven by highlight state.
 * 2. Contour lines (vtkCutter + vtkActor per structure on renderer layer 0) —
 *    always-visible outlines that update when the slice changes.
 *
 * A single onSliceChanged subscription drives all cutters, instead of
 * N individual subscriptions.
 */

import vtkPiecewiseFunction from '@kitware/vtk.js/Common/DataModel/PiecewiseFunction';
import vtkPlane from '@kitware/vtk.js/Common/DataModel/Plane';
// @ts-expect-error — vtkCutter has no type declarations
import vtkCutter from '@kitware/vtk.js/Filters/Core/Cutter';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import vtkImageResliceMapper from '@kitware/vtk.js/Rendering/Core/ImageResliceMapper';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import type vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

import { DosimetryStateService } from '../../../workflows/oncology/dosimetry/services/dosimetry-state-service';
import type { RGB } from '../../domain/types';
import { BaseViewport } from '../../viewports';
import { SliceViewport2D } from '../../viewports/slice-viewport-2d';
import type {
    DisplayFeatureType,
    IDisplayFeature,
    IDisplayObject,
    StructureSetDisplayProperties,
    ViewMode,
} from '../types';
import type { StructureSetFeatureData } from './structure-set-types';

/** Label map overlay opacity when a structure is hovered. */
const LABEL_MAP_HOVERED_OPACITY = 0.5;
/** Label map overlay opacity when a structure is selected (but not hovered). */
const LABEL_MAP_SELECTED_OPACITY = 0.3;
/** Contour line width in pixels. */
const CONTOUR_LINE_DEFAULT_WIDTH = 1.5;
/** Contour line width in pixels when a structure is selected. */
const CONTOUR_LINE_SELECTED_WIDTH = 2.5;

interface ContourEntry {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cutter: any;
    mapper: vtkMapper;
    actor: vtkActor;
}

export class StructureSetDisplayObject2D implements IDisplayObject {
    readonly feature: IDisplayFeature<DisplayFeatureType.STRUCTURE_SET>;
    readonly viewMode: ViewMode;

    private viewport: BaseViewport | null = null;
    private contourRenderer: vtkRenderer | null = null;
    private labelMapRenderer: vtkRenderer | null = null;

    // Label map overlay pipeline
    private imageSlice: vtkImageSlice | null = null;
    private resliceMapper: vtkImageResliceMapper | null = null;
    private colorFunction: vtkColorTransferFunction | null = null;
    private opacityFunction: vtkPiecewiseFunction | null = null;

    // Contour pipelines: labelValue → entry
    private contours = new Map<number, ContourEntry>();

    private unsubscribers: Array<() => void> = [];

    constructor(feature: IDisplayFeature<DisplayFeatureType.STRUCTURE_SET>, viewMode: ViewMode) {
        this.feature = feature;
        this.viewMode = viewMode;
    }

    private get data(): StructureSetFeatureData {
        return this.feature.data as StructureSetFeatureData;
    }

    private get props(): StructureSetDisplayProperties {
        return this.feature.properties;
    }

    initialize(viewport: BaseViewport): void {
        this.viewport = viewport;
        this.contourRenderer = viewport.getRenderer(1);
        this.labelMapRenderer = viewport.getRenderer(1);

        this.createLabelMapPipeline();
        this.createContoursFromLoadedMeshes();

        if (viewport instanceof SliceViewport2D) {
            this.unsubscribers.push(viewport.onSliceChanged(() => this.onSliceChanged()));
        }
    }

    update(): void {
        // Create label map pipeline if not yet created (data arrived late)
        if (!this.imageSlice && this.data.labelMap && this.labelMapRenderer) {
            this.createLabelMapPipeline();
        }

        // Create contour actors for any newly loaded meshes
        this.createContoursFromLoadedMeshes();

        this.applyProperties();
        this.viewport?.render();
    }

    dispose(): void {
        for (const unsub of this.unsubscribers) unsub();
        this.unsubscribers = [];

        // Dispose contour pipelines
        for (const entry of this.contours.values()) {
            this.contourRenderer?.removeActor(entry.actor);
            entry.actor.delete();
            entry.mapper.delete();
            entry.cutter.delete();
        }
        this.contours.clear();

        // Dispose label map pipeline
        if (this.labelMapRenderer && this.imageSlice) {
            this.labelMapRenderer.removeActor(this.imageSlice);
        }
        this.imageSlice?.delete();
        this.resliceMapper?.delete();
        this.colorFunction?.delete();
        this.opacityFunction?.delete();

        this.imageSlice = null;
        this.resliceMapper = null;
        this.colorFunction = null;
        this.opacityFunction = null;
        this.contourRenderer = null;
        this.labelMapRenderer = null;
        this.viewport = null;
    }

    getActors(): vtkActor[] {
        return [...this.contours.values()].map((e) => e.actor);
    }

    // ── Label Map Pipeline ──

    private createLabelMapPipeline(): void {
        if (!this.labelMapRenderer || !this.viewport) return;
        const { labelMap } = this.data;
        if (!labelMap) return;

        const slicePlane = this.viewport instanceof SliceViewport2D ? this.viewport.getSlicePlane() : null;
        if (!slicePlane) return;

        this.resliceMapper = vtkImageResliceMapper.newInstance();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.resliceMapper as any).setInputData(labelMap.imageData);
        this.resliceMapper.setSlicePlane(slicePlane);

        // Use transparent black background color to avoid artifacts
        // when slicing outside label map volume
        this.resliceMapper.setBackgroundColor([0, 0, 0, 0]);

        this.colorFunction = vtkColorTransferFunction.newInstance();
        this.opacityFunction = vtkPiecewiseFunction.newInstance();
        this.buildTransferFunctions();

        this.imageSlice = vtkImageSlice.newInstance();
        this.imageSlice.setMapper(this.resliceMapper);

        const property = this.imageSlice.getProperty();
        property.setRGBTransferFunction(0, this.colorFunction);
        property.setScalarOpacity(0, this.opacityFunction);
        property.setInterpolationTypeToLinear();
        property.setUseLookupTableScalarRange(true);
        property.setIndependentComponents(true);

        this.labelMapRenderer.addActor(this.imageSlice);
    }

    private buildTransferFunctions(): void {
        if (!this.colorFunction || !this.opacityFunction) return;

        const { colors, kind, hoveredKey, selectedKeys } = this.props;

        this.colorFunction.removeAllPoints();
        this.opacityFunction.removeAllPoints();

        if (colors.size === 0) {
            this.colorFunction.addRGBPoint(0, 0, 0, 0);
            this.opacityFunction.addPoint(0, 0);
            return;
        }

        const sortedLabels = Array.from(colors.keys()).sort((a, b) => a - b);

        // Background label 0: transparent black
        this.colorFunction.addRGBPoint(0, 0, 0, 0);
        this.opacityFunction.addPoint(0, 0);

        // Add a black region before the first label
        const firstLabel = sortedLabels[0];
        if (firstLabel > 0) {
            this.colorFunction.addRGBPoint(firstLabel - 0.5, 0, 0, 0);
        }

        for (let i = 0; i < sortedLabels.length; i++) {
            const lv = sortedLabels[i];
            const [r, g, b] = colors.get(lv)!;

            this.colorFunction.addRGBPoint(lv, r, g, b);

            // Insert sharp-edge transition points between adjacent labels
            if (i < sortedLabels.length - 1) {
                const nextLv = sortedLabels[i + 1];
                const mid = (lv + nextLv) / 2;
                this.colorFunction.addRGBPoint(mid - 0.1, r, g, b);
                this.colorFunction.addRGBPoint(mid + 0.1, 0, 0, 0);
            }

            const key = DosimetryStateService.structureKey(kind, lv);
            let opacity = 0;
            if (key === hoveredKey) opacity = LABEL_MAP_HOVERED_OPACITY;
            else if (selectedKeys.has(key)) opacity = LABEL_MAP_SELECTED_OPACITY;
            this.opacityFunction.addPoint(lv, opacity);
        }

        // Add a black region after the last label
        const lastLabel = sortedLabels[sortedLabels.length - 1];
        const [lastR, lastG, lastB] = colors.get(lastLabel)!;
        if (lastLabel < 255) {
            this.colorFunction.addRGBPoint(lastLabel + 0.5, lastR, lastG, lastB);
            this.colorFunction.addRGBPoint(255, 0, 0, 0);
        }
    }

    // ── Contour Pipelines ──

    private createContoursFromLoadedMeshes(): void {
        if (!this.contourRenderer || !this.viewport) return;

        const cutPlane =
            this.viewport instanceof SliceViewport2D ? this.viewport.getSlicePlane() : vtkPlane.newInstance();

        if (!cutPlane) return;

        for (const [labelValue, polyData] of this.data.meshes) {
            if (this.contours.has(labelValue)) continue;

            const cutter = vtkCutter.newInstance();
            cutter.setCutFunction(cutPlane);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (cutter as any).setInputData(polyData);

            const mapper = vtkMapper.newInstance();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (mapper as any).setInputConnection(cutter.getOutputPort());

            const actor = vtkActor.newInstance();
            actor.setMapper(mapper);

            this.applyContourProperties(labelValue, actor);
            this.contourRenderer.addActor(actor);
            this.contours.set(labelValue, { cutter, mapper, actor });
        }
    }

    private applyContourProperties(labelValue: number, actor: vtkActor): void {
        const { kind, selectedKeys } = this.props;
        const key = DosimetryStateService.structureKey(kind, labelValue);
        const selected = selectedKeys.has(key);

        const color: RGB = this.props.colors.get(labelValue) ?? [0.6, 0.6, 0.6];
        const property = actor.getProperty();
        property.setColor(color[0], color[1], color[2]);
        property.setOpacity(1.0);
        property.setLighting(false);
        property.setLineWidth(selected ? CONTOUR_LINE_SELECTED_WIDTH : CONTOUR_LINE_DEFAULT_WIDTH);
        property.setRepresentationToWireframe();
    }

    // ── Apply All Properties ──

    private applyProperties(): void {
        // Update label map transfer functions (handles highlight)
        this.buildTransferFunctions();

        // Update contour colors (in case colors changed)
        for (const [labelValue, entry] of this.contours) {
            this.applyContourProperties(labelValue, entry.actor);
        }
    }

    // ── Slice Change ──

    private onSliceChanged(): void {
        for (const entry of this.contours.values()) {
            entry.cutter.modified();
        }
    }
}
